/**
 * Settings → Watching (SPEC `New Post Watching` § Settings modal, §5.1 #68-#76).
 *
 * Top to bottom: master toggle + snooze · schedule · availability · cost estimate · stats ·
 * Telegram.
 *
 * Two save styles, as the SPEC asks. The **master toggle writes immediately** — it is a switch, and
 * a switch that needs a Save is a switch that lies. Everything else is one draft behind one Save
 * (the General-tab pattern), because a half-typed cadence must never be the interval a paid check
 * runs at, and because schedule, availability and cost-per-check are read together by the estimate.
 * That Save sends a **diff**, again like General: #69 is a partial patch, so a field this tab did
 * not change is not rewritten — an agent or a second tab may have written the blob meanwhile.
 *
 * The **cost card is the reason the draft exists**: it posts the whole draft to #72 on a 500 ms
 * debounce, so an expensive value is priced before it is saved, not after. Every figure it renders
 * — money, month estimate, breakdown lines, "last check" — is a server string printed verbatim
 * (§12.5). Nothing here multiplies anything.
 */

import { useEffect, useMemo, useState } from 'react';
import {
  WATCH_CADENCE_MAX_MINUTES,
  WATCH_CADENCE_MIN_MINUTES,
  WEEKDAY_KEYS,
  type WatchAvailability,
  type WatchEstimateBody,
  type WatchSettings,
  type WatchSettingsUpdate,
  type WatchStateResponse,
  type WeekdayKey,
} from '@lw/shared';
import { describeError } from '../../api/client';
import { ErrorState, Spinner } from '../../components';
import { useDebouncedCallback } from '../../lib/hooks';
import { CadenceQuickPicks } from './CadenceQuickPicks';
import { SnoozePicks } from './SnoozePicks';
import { WatchTelegramSection } from './WatchTelegramSection';
import { useAppTimezone } from './useSettings';
import {
  useUpdateWatchSettings,
  useWatchEstimate,
  useWatchQuery,
  useWatchSnooze,
} from './useWatch';

/** How long the cost card waits after the last keystroke before pricing the draft. */
const ESTIMATE_DEBOUNCE_MS = 500;

const WEEKDAY_LABEL: Record<WeekdayKey, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

/**
 * `<input type="time">` tops out at 23:59, but a full day is spelled `00:00-24:00`. The two are the
 * same instant, so the sentinel is swapped at the edge in both directions and nothing downstream
 * ever sees a value the picker cannot hold.
 */
const END_OF_DAY = '24:00';
const MIDNIGHT = '00:00';

type DayDraft = { off: boolean; start: string; end: string };

export type Draft = {
  cadenceMinutes: string;
  smart: boolean;
  hotCadenceMinutes: string;
  coldCadenceMinutes: string;
  availability: Record<WeekdayKey, DayDraft>;
  costPerCheckUsd: string;
};

export function toDraft(settings: WatchSettings): Draft {
  const availability = {} as Record<WeekdayKey, DayDraft>;
  for (const day of WEEKDAY_KEYS) {
    const range = settings.availability[day];
    availability[day] =
      range === null
        ? { off: true, start: MIDNIGHT, end: MIDNIGHT }
        : { off: false, start: range.start, end: range.end === END_OF_DAY ? MIDNIGHT : range.end };
  }
  return {
    cadenceMinutes: String(settings.cadenceMinutes),
    smart: settings.smart,
    hotCadenceMinutes: String(settings.hotCadenceMinutes),
    coldCadenceMinutes: String(settings.coldCadenceMinutes),
    availability,
    costPerCheckUsd: String(settings.costPerCheckUsd),
  };
}

const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

function cadenceOf(raw: string): number | null {
  const parsed = Number(raw);
  if (raw.trim().length === 0 || !Number.isInteger(parsed)) return null;
  if (parsed < WATCH_CADENCE_MIN_MINUTES || parsed > WATCH_CADENCE_MAX_MINUTES) return null;
  return parsed;
}

function availabilityOf(draft: Draft): WatchAvailability | null {
  const availability = {} as WatchAvailability;
  for (const day of WEEKDAY_KEYS) {
    const row = draft.availability[day];
    if (row.off) {
      availability[day] = null;
      continue;
    }
    if (!TIME_PATTERN.test(row.start) || !TIME_PATTERN.test(row.end)) return null;
    const end = row.end === MIDNIGHT ? END_OF_DAY : row.end;
    // `start === end` is meaningless rather than empty-or-whole, and the server refuses it.
    if (row.start === end) return null;
    availability[day] = { start: row.start, end };
  }
  return availability;
}

/** The six saveable fields, parsed: what #72 prices and what the Save diff is computed from. */
export type DraftValues = {
  cadenceMinutes: number;
  smart: boolean;
  hotCadenceMinutes: number;
  coldCadenceMinutes: number;
  availability: WatchAvailability;
  costPerCheckUsd: number;
};

/**
 * `null` when the draft is not a legal set of values — Save stays disabled and the fields say why.
 *
 * The hot/quiet pair is read from the draft **only while the smart block is on screen**. With smart
 * off those two inputs are unmounted, so the saved pair stands in and whatever the draft still holds
 * is neither validated nor written: otherwise a value the user cannot see — blank, out of range, or
 * hot slower than quiet — would disable Save for the whole tab with no visible offending field.
 * (#69 re-checks `hot <= cold` on the value it merges, so the rule survives either way.)
 */
export function draftValues(draft: Draft, settings: WatchSettings): DraftValues | null {
  const cadenceMinutes = cadenceOf(draft.cadenceMinutes);
  const hotCadenceMinutes = draft.smart
    ? cadenceOf(draft.hotCadenceMinutes)
    : settings.hotCadenceMinutes;
  const coldCadenceMinutes = draft.smart
    ? cadenceOf(draft.coldCadenceMinutes)
    : settings.coldCadenceMinutes;
  const costPerCheckUsd = Number(draft.costPerCheckUsd);
  const availability = availabilityOf(draft);
  if (cadenceMinutes === null || hotCadenceMinutes === null || coldCadenceMinutes === null) {
    return null;
  }
  if (draft.smart && hotCadenceMinutes > coldCadenceMinutes) return null;
  if (draft.costPerCheckUsd.trim().length === 0 || !(costPerCheckUsd > 0)) return null;
  if (availability === null) return null;
  return {
    cadenceMinutes,
    smart: draft.smart,
    hotCadenceMinutes,
    coldCadenceMinutes,
    availability,
    costPerCheckUsd,
  };
}

function sameAvailability(left: WatchAvailability, right: WatchAvailability): boolean {
  return WEEKDAY_KEYS.every((day) => {
    const a = left[day];
    const b = right[day];
    if (a === null || b === null) return a === b;
    return a.start === b.start && a.end === b.end;
  });
}

/**
 * The #69 body: **only the fields that differ** from what the server last told us. #69 is a partial
 * patch precisely so a Save here never rewrites a field the user did not touch — an agent, a script
 * or a second tab may have written the blob while this one sat open, and shipping all six would
 * silently revert it. `null` = nothing changed, so Save is disabled exactly as the General tab's is
 * (the server refuses an empty body anyway).
 */
export function toBody(values: DraftValues, settings: WatchSettings): WatchSettingsUpdate | null {
  const next: WatchSettingsUpdate = {};
  if (values.cadenceMinutes !== settings.cadenceMinutes) next.cadenceMinutes = values.cadenceMinutes;
  if (values.smart !== settings.smart) next.smart = values.smart;
  if (values.hotCadenceMinutes !== settings.hotCadenceMinutes) {
    next.hotCadenceMinutes = values.hotCadenceMinutes;
  }
  if (values.coldCadenceMinutes !== settings.coldCadenceMinutes) {
    next.coldCadenceMinutes = values.coldCadenceMinutes;
  }
  if (!sameAvailability(values.availability, settings.availability)) {
    next.availability = values.availability;
  }
  if (values.costPerCheckUsd !== settings.costPerCheckUsd) {
    next.costPerCheckUsd = values.costPerCheckUsd;
  }
  return Object.keys(next).length > 0 ? next : null;
}

export function WatchingSection() {
  const query = useWatchQuery();
  const state = query.data;

  if (query.isPending) return <Spinner size="lg" label="Loading watching settings…" />;
  if (query.isError) {
    return (
      <ErrorState
        title="Couldn't load the watching settings"
        error={query.error}
        onRetry={() => void query.refetch()}
        retrying={query.isFetching}
      />
    );
  }
  if (state === undefined) return null;

  return <WatchingTab settings={state.settings} state={state} />;
}

type WatchingTabProps = {
  settings: WatchSettings;
  state: WatchStateResponse;
};

function WatchingTab({ settings, state }: WatchingTabProps) {
  const timezone = useAppTimezone();
  const update = useUpdateWatchSettings();
  const snooze = useWatchSnooze();
  const estimate = useWatchEstimate();
  // Seeded once, on mount — the tab unmounts with its section, so re-opening re-seeds naturally.
  const [draft, setDraft] = useState<Draft>(() => toDraft(settings));
  const [saved, setSaved] = useState(false);

  // Two derivations, on purpose: the estimate prices the **whole** draft (a cost needs every field,
  // changed or not), while Save ships only the difference.
  const values = useMemo(() => draftValues(draft, settings), [draft, settings]);
  const body = useMemo(
    () => (values === null ? null : toBody(values, settings)),
    [values, settings],
  );

  const patch = (changes: Partial<Draft>) => {
    setSaved(false);
    setDraft((current) => ({ ...current, ...changes }));
  };

  const patchDay = (day: WeekdayKey, changes: Partial<DayDraft>) => {
    setSaved(false);
    setDraft((current) => ({
      ...current,
      availability: {
        ...current.availability,
        [day]: { ...current.availability[day], ...changes },
      },
    }));
  };

  /* --------------------------------------------------------------- estimate */

  // The card prices the **whole** draft — a cost needs every field, not just the changed ones —
  // plus the master flag, which this tab's Save never writes.
  const estimateBody: WatchEstimateBody | null =
    values === null ? null : { enabled: settings.enabled, ...values };

  const price = estimate.mutate;
  const priceDebounced = useDebouncedCallback((next: WatchEstimateBody) => {
    price(next);
  }, ESTIMATE_DEBOUNCE_MS);

  // The serialized draft is the dependency: re-pricing must follow the *values*, not the identity
  // of the object the render happened to build.
  const estimateKey = estimateBody === null ? null : JSON.stringify(estimateBody);
  useEffect(() => {
    if (estimateKey === null) return;
    priceDebounced(JSON.parse(estimateKey) as WatchEstimateBody);
  }, [estimateKey, priceDebounced]);

  /* ------------------------------------------------------------------- save */

  const save = () => {
    if (body === null) return;
    update.mutate(body, {
      onSuccess: (next) => {
        setDraft(toDraft(next.settings));
        setSaved(true);
      },
    });
  };

  const toggleMaster = (enabled: boolean) => {
    setSaved(false);
    update.mutate({ enabled });
  };

  const hotOverCold =
    cadenceOf(draft.hotCadenceMinutes) !== null &&
    cadenceOf(draft.coldCadenceMinutes) !== null &&
    Number(draft.hotCadenceMinutes) > Number(draft.coldCadenceMinutes);

  return (
    <>
      {update.isError ? (
        <p className="form-error" role="alert">
          {describeError(update.error)}
        </p>
      ) : null}

      {/* ------------------------------------------------------- 1 · master */}
      <section className="settings-section">
        <h3 className="settings-title">Watching</h3>
        <label className="toggle">
          <input
            type="checkbox"
            checked={settings.enabled}
            disabled={update.isPending}
            onChange={(event) => toggleMaster(event.target.checked)}
          />
          <span>Watching enabled</span>
        </label>
        <p className="field__hint">
          Frequent lightweight checks of the accounts you mark, so a new post reaches you while a
          comment is still early. Off pauses the whole subsystem — settings are kept and nothing is
          spent.
        </p>
        {/* #94 — the snooze row. A snooze is not a setting: it never enters the draft, and the
            server owns both the instant and the sentence that names it. */}
        {state.snooze !== null ? (
          <div className="watch-snooze">
            <span>{state.snooze.display}</span>
            <button
              type="button"
              className="btn-ghost btn-ghost--sm"
              disabled={snooze.isPending}
              onClick={() => snooze.mutate(null)}
            >
              Resume
            </button>
          </div>
        ) : settings.enabled ? (
          <div className="watch-snooze">
            <SnoozePicks
              onPick={(preset) => snooze.mutate(preset)}
              disabled={snooze.isPending}
              ariaLabel="Snooze watching"
            />
            <span className="field__hint">
              Pauses every check for a while, without touching a setting. It expires on its own.
            </span>
          </div>
        ) : null}
        {snooze.isError ? (
          <p className="form-error" role="alert">
            {describeError(snooze.error)}
          </p>
        ) : null}
        {state.pausedReasons.map((reason) => (
          <p className="settings-warn" role="alert" key={reason}>
            {reason}
          </p>
        ))}
      </section>

      {/* ----------------------------------------------------- 2 · schedule */}
      <section className="settings-section">
        <h3 className="settings-title">Schedule</h3>

        <div className="field watch-field">
          <label className="field__label" htmlFor="watch-cadence">
            Check every (minutes)
          </label>
          <input
            id="watch-cadence"
            className="input"
            type="number"
            min={WATCH_CADENCE_MIN_MINUTES}
            max={WATCH_CADENCE_MAX_MINUTES}
            step="1"
            value={draft.cadenceMinutes}
            onChange={(event) => patch({ cadenceMinutes: event.target.value })}
          />
          <CadenceQuickPicks
            value={draft.cadenceMinutes}
            onPick={(minutes) => patch({ cadenceMinutes: minutes })}
            ariaLabel="Cadence presets"
          />
          {cadenceOf(draft.cadenceMinutes) === null ? (
            <span className="settings-bad">
              Enter a whole number between {WATCH_CADENCE_MIN_MINUTES} and{' '}
              {WATCH_CADENCE_MAX_MINUTES}.
            </span>
          ) : (
            <span className="field__hint">
              A group may check faster than this; the fastest source wins for a profile in several.
            </span>
          )}
        </div>

        <label className="toggle">
          <input
            type="checkbox"
            checked={draft.smart}
            onChange={(event) => patch({ smart: event.target.checked })}
          />
          <span>Smart schedule</span>
        </label>
        <p className="field__hint">
          Hot hours are learned from each account's posting pattern. An account with too little
          history keeps the fixed cadence above.
        </p>

        {draft.smart ? (
          <div className="settings-grid">
            <div className="field watch-field">
              <label className="field__label" htmlFor="watch-hot">
                Hot hours — check every (minutes)
              </label>
              <input
                id="watch-hot"
                className="input"
                type="number"
                min={WATCH_CADENCE_MIN_MINUTES}
                max={WATCH_CADENCE_MAX_MINUTES}
                step="1"
                value={draft.hotCadenceMinutes}
                onChange={(event) => patch({ hotCadenceMinutes: event.target.value })}
              />
              <CadenceQuickPicks
                value={draft.hotCadenceMinutes}
                onPick={(minutes) => patch({ hotCadenceMinutes: minutes })}
                ariaLabel="Hot-hour cadence presets"
              />
              {cadenceOf(draft.hotCadenceMinutes) === null ? (
                <span className="settings-bad">
                  Enter a whole number between {WATCH_CADENCE_MIN_MINUTES} and{' '}
                  {WATCH_CADENCE_MAX_MINUTES}.
                </span>
              ) : null}
            </div>

            <div className="field watch-field">
              <label className="field__label" htmlFor="watch-cold">
                Quiet hours — check every (minutes)
              </label>
              <input
                id="watch-cold"
                className="input"
                type="number"
                min={WATCH_CADENCE_MIN_MINUTES}
                max={WATCH_CADENCE_MAX_MINUTES}
                step="1"
                value={draft.coldCadenceMinutes}
                onChange={(event) => patch({ coldCadenceMinutes: event.target.value })}
              />
              <CadenceQuickPicks
                value={draft.coldCadenceMinutes}
                onPick={(minutes) => patch({ coldCadenceMinutes: minutes })}
                ariaLabel="Quiet-hour cadence presets"
              />
              {cadenceOf(draft.coldCadenceMinutes) === null ? (
                <span className="settings-bad">
                  Enter a whole number between {WATCH_CADENCE_MIN_MINUTES} and{' '}
                  {WATCH_CADENCE_MAX_MINUTES}.
                </span>
              ) : hotOverCold ? (
                <span className="settings-bad">
                  Quiet hours cannot check more often than hot hours.
                </span>
              ) : null}
            </div>
          </div>
        ) : null}
      </section>

      {/* ------------------------------------------------- 3 · availability */}
      <section className="settings-section">
        <h3 className="settings-title">Availability</h3>
        <p className="field__hint">
          Outside these windows nothing is checked and nothing is spent. Times are in the app
          timezone ({timezone}); an end earlier than the start runs overnight, and an end of 00:00
          means midnight at the close of that day.
        </p>

        <div className="watch-week">
          {WEEKDAY_KEYS.map((day) => {
            const row = draft.availability[day];
            const bad =
              !row.off &&
              (!TIME_PATTERN.test(row.start) ||
                !TIME_PATTERN.test(row.end) ||
                row.start === (row.end === MIDNIGHT ? END_OF_DAY : row.end));
            return (
              <div className="watch-day" key={day}>
                <span className="watch-day__name">{WEEKDAY_LABEL[day]}</span>
                <label className="toggle watch-day__off">
                  <input
                    type="checkbox"
                    checked={row.off}
                    onChange={(event) => patchDay(day, { off: event.target.checked })}
                  />
                  <span>Off</span>
                </label>
                {row.off ? (
                  <span className="faint watch-day__note">No checks this day.</span>
                ) : (
                  <span className="watch-day__range">
                    <input
                      className="input watch-time"
                      type="time"
                      aria-label={`${WEEKDAY_LABEL[day]} start`}
                      value={row.start}
                      onChange={(event) => patchDay(day, { start: event.target.value })}
                    />
                    <span className="faint">to</span>
                    <input
                      className="input watch-time"
                      type="time"
                      aria-label={`${WEEKDAY_LABEL[day]} end`}
                      value={row.end}
                      onChange={(event) => patchDay(day, { end: event.target.value })}
                    />
                    {bad ? <span className="settings-bad">Pick two different times.</span> : null}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* --------------------------------------------------------- 4 · cost */}
      <section className="settings-section">
        <h3 className="settings-title">Cost estimate</h3>
        <div className="watch-cost">
          <div className="watch-cost__headline" role="status">
            {estimate.isPending ? (
              <>
                <Spinner size="sm" />
                <span className="faint">Pricing…</span>
              </>
            ) : estimate.data !== undefined ? (
              estimate.data.display
            ) : estimate.isError ? (
              <span className="settings-bad">{describeError(estimate.error)}</span>
            ) : (
              <span className="faint">—</span>
            )}
          </div>
          {estimate.data !== undefined && estimate.data.breakdown.length > 0 ? (
            <dl className="watch-cost__breakdown">
              {estimate.data.breakdown.map((line) => (
                <div key={line.label}>
                  <dt>{line.label}</dt>
                  <dd>{line.display}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          <div className="field watch-field">
            <label className="field__label" htmlFor="watch-cost-per-check">
              Cost per check (USD)
            </label>
            <input
              id="watch-cost-per-check"
              className="input"
              type="number"
              min="0"
              step="0.0001"
              value={draft.costPerCheckUsd}
              onChange={(event) => patch({ costPerCheckUsd: event.target.value })}
            />
            {draft.costPerCheckUsd.trim().length === 0 || !(Number(draft.costPerCheckUsd) > 0) ? (
              <span className="settings-bad">Enter an amount greater than zero.</span>
            ) : (
              <span className="field__hint">What your scraper charges for one check.</span>
            )}
          </div>
        </div>
      </section>

      <div className="notif-save">
        <button
          type="button"
          className="btn-primary"
          onClick={save}
          disabled={body === null || update.isPending}
        >
          {update.isPending ? <Spinner size="sm" /> : null}
          Save watching settings
        </button>
        {saved && !update.isPending ? <span className="muted dialog-hint">Saved.</span> : null}
        {/* A valid draft with nothing changed reads as a disabled button and no complaint — the
            General tab's contract. Only a broken field earns the sentence. */}
        {values === null ? (
          <span className="settings-bad">
            Fix the fields above — cadences are whole minutes, hot is not slower than quiet, every
            open day has two different times, and the cost per check is above zero.
          </span>
        ) : (
          <span className="field__hint">
            Saves the schedule, the availability week and the cost per check together.
          </span>
        )}
      </div>

      {/* -------------------------------------------------------- 5 · stats */}
      <section className="settings-section">
        <h3 className="settings-title">Activity</h3>
        <dl className="settings-readonly">
          <div>
            <dt>Checks today</dt>
            <dd>{state.stats.checksToday}</dd>
          </div>
          <div>
            <dt>Last check</dt>
            <dd>{state.stats.lastCheckAtDisplay}</dd>
          </div>
          <div>
            <dt>Detections today</dt>
            <dd>{state.stats.detectionsToday}</dd>
          </div>
          <div>
            <dt>Notifications today</dt>
            <dd>{state.stats.notifiedToday}</dd>
          </div>
          <div>
            <dt>Spend this month</dt>
            <dd>{state.stats.monthEstimate.display}</dd>
          </div>
        </dl>
      </section>

      {/* ----------------------------------------------------- 6 · telegram */}
      <WatchTelegramSection />
    </>
  );
}

export default WatchingSection;
